import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import { Button, ButtonGroup, ButtonHandle } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { tableIcon, alignJustifyIcon, chevronDownIcon, arrowRotateCwIcon, arrowUpIcon, arrowDownIcon, xIcon } from '@progress/kendo-svg-icons'
import AssetsDashboardTileView from "../Components/AssetsDashboardTileView";
import AssetsDashboardTableView from "../Components/AssetsDashboardTableView";
import AssetSelectedPage from "./AssetSelectedPage";
import { useNavigate } from "react-router-dom";
import useAssetStore, {
  AssetsDashboardListOrderByField,
} from "../../../../Zustand/hmeDashboardStore";
import { ClientRoutes } from "../../../../Constants/ClientRoutingConstants";
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import { UseQueryResult } from "react-query";
import AssetImageReadDto from "../../../../Models/Assets/AssetImageReadDto";
import { useState, useMemo, useCallback, useRef } from "react";
import { InputClearValue, InputSeparator, TextBox, TextBoxProps } from "@progress/kendo-react-inputs";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import { SvgIcon } from "@progress/kendo-react-common";
import { Popup } from "@progress/kendo-react-popup";
import SelectAllMultiSelectTree from "../../../Shared/Components/SelectAllMultiSelectTree";
import SiteWorkbookTab from "./SiteWorkbookTab";
import DashboardNotesTab from "./DashboardNotesTab";
import SiteInsightsTab from "./SiteInsightsTab";
import useIdentityStore from "../../../../Zustand/identityStore";
import DashboardPanel from "../../../Shared/Components/DashboardPanel";
import ClearCacheButton from "../../../Shared/Components/ClearCacheButton";
import assetsDashboardApiService from "../Utilities/AssetsDashboardApiService";

type OrderByField = AssetsDashboardListOrderByField;

// Sort ranks for the order-by comparator (module-level so they aren't reallocated per comparison).
const CONNECTIVITY_SORT_ORDER: Record<string, number> = { Offline: 0, Partial: 1, Online: 2 };
const COMPLIANCE_SORT_ORDER: Record<string, number> = { Unknown: 0, NonCompliant: 1, PartiallyCompliant: 2, Compliant: 3 };

interface Props {
  assetsMetricsData: AssetMetricsReadDto[];
  imagesQueries: UseQueryResult<AssetImageReadDto>[];
  onRefresh: () => Promise<void>;
  isFetching: boolean;
}

export default function AssetsDashboardContainer({ 
  assetsMetricsData, 
  imagesQueries, 
  onRefresh,
  isFetching
}: Props) {
  const localizationService = useLocalization();
  const navigate = useNavigate();
  
  // Get all needed states from the store
  const selectedAsset = useAssetStore(state => state.selectedAsset);
  const setSelectedAsset = useAssetStore(state => state.setSelectedAsset);
  const isTileViewSelected = useAssetStore(state => state.isTileViewSelected);
  const setIsTileViewSelected = useAssetStore(state => state.setIsTileViewSelected);
  const assetsListSearchText = useAssetStore(state => state.assetsListSearchText);
  const setAssetsListSearchText = useAssetStore(state => state.setAssetsListSearchText);
  const complianceValues = useAssetStore(state => state.assetsListComplianceValues);
  const setAssetsListComplianceValues = useAssetStore(state => state.setAssetsListComplianceValues);
  const connectivityValues = useAssetStore(state => state.assetsListConnectivityValues);
  const setAssetsListConnectivityValues = useAssetStore(state => state.setAssetsListConnectivityValues);
  const orderByList = useAssetStore(state => state.assetsListOrderBy);
  const setAssetsListOrderBy = useAssetStore(state => state.setAssetsListOrderBy);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const searchText = assetsListSearchText as TextBoxProps["value"];
  const [orderByPopupOpen, setOrderByPopupOpen] = useState(false);
  const orderByAnchorRef = useRef<ButtonHandle>(null);
  // 0 = Overview (current tile/table view), 1 = Notes, 2 = Site Workbook, 3 = Insights (SuperAdmin only).
  const [dashboardTab, setDashboardTab] = useState(0);
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const isSuperAdmin = currentUserRole === "SuperAdmin";

  const handleViewChange = (view: boolean) => {
    setIsTileViewSelected(view);
  };

  const handleAssetSelect = (asset: AssetMetricsReadDto) => {
    setSelectedAsset(asset);
    if (asset.id) {
      navigate(`${ClientRoutes.AssetsDashboardPage}/${asset.id}`, {
        state: {
          alert: asset.alert,
          compliance: asset.complianceStatus,
          connectivity: asset.connectivityStatus
        }
      });
    }
  };
  

  const selectAllL = localizationService.toLanguageString('custom.selectAll', enMessages.custom.selectAll);

  const complianceOptions = [
    { id: 'Compliant', text: localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant) },
    { id: 'NonCompliant', text: localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant) },
    { id: 'PartiallyCompliant', text: localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant) },
    { id: 'Unknown', text: localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown) },
  ];

  const connectivityOptions = [
    { id: 'Online', text: localizationService.toLanguageString('custom.online', enMessages.custom.online) },
    { id: 'Offline', text: localizationService.toLanguageString('custom.offline', enMessages.custom.offline) },
    { id: 'Partial', text: localizationService.toLanguageString('custom.partialConnected', enMessages.custom.partialConnected) },
  ];

  const sortAscendingL = localizationService.toLanguageString('custom.sortAscending', enMessages.custom.sortAscending);
  const sortDescendingL = localizationService.toLanguageString('custom.sortDescending', enMessages.custom.sortDescending);

  // Sortable fields shown in the order-by popup — same Button+Tooltip rendered for each.
  const orderByOptions: { field: OrderByField; label: string }[] = [
    { field: 'Name', label: localizationService.toLanguageString('custom.name', enMessages.custom.name) },
    { field: 'Model', label: localizationService.toLanguageString('custom.model', enMessages.custom.model) },
    { field: 'Compliance', label: localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance) },
    { field: 'Connectivity', label: localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity) },
  ];

  const getOrderByEntry = (field: OrderByField) =>
    orderByList.find(o => o.field === field);

  const getOrderByIndex = (field: OrderByField): number =>
    orderByList.findIndex(o => o.field === field) + 1;

  const handleOrderByClick = (field: OrderByField) => {
    setAssetsListOrderBy((prev) => {
      const existing = prev.find(o => o.field === field);
      if (!existing) {
        return [...prev, { field, direction: 'asc' }];
      }
      if (existing.direction === 'asc') {
        return prev.map(o => o.field === field ? { ...o, direction: 'desc' } : o);
      }
      return prev.filter(o => o.field !== field);
    });
  };

  const filteredAssetsByComplianceAndConnectivity = useMemo(() => {
    if (!assetsMetricsData) {
      return [];
    }

    return assetsMetricsData.filter(asset =>
      complianceValues.some((value) =>
        value.replace("-", "") === asset.complianceStatus
      ) &&
      connectivityValues.some((value) =>
        value.replace("-", "") === asset.connectivityStatus
      )
    );
  }, [assetsMetricsData, complianceValues, connectivityValues]);

  const getCompareValue = (asset: AssetMetricsReadDto, field: OrderByField): number | string => {
    if (field === 'Compliance') return COMPLIANCE_SORT_ORDER[asset.complianceStatus] ?? 3;
    if (field === 'Connectivity') return CONNECTIVITY_SORT_ORDER[asset.connectivityStatus] ?? 3;
    if (field === 'Model') return asset.model || '';
    return asset.name || '';
  };

  const sortedAssets = useMemo(() => {
    const assets = filteredAssetsByComplianceAndConnectivity;
    if (!assets.length || !orderByList.length) return assets;

    return [...assets].sort((a, b) => {
      for (const { field, direction } of orderByList) {
        const aVal = getCompareValue(a, field);
        const bVal = getCompareValue(b, field);
        let cmp = typeof aVal === 'string' && typeof bVal === 'string'
          ? aVal.localeCompare(bVal)
          : (aVal as number) - (bVal as number);
        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [filteredAssetsByComplianceAndConnectivity, orderByList]);

  const searchedAssets = useMemo(() => {
    const assets = sortedAssets;
    if (!searchText || typeof searchText !== 'string' || !searchText.trim()) return assets;
    const term = searchText.trim().toLowerCase();
    const searchableFields: (keyof AssetMetricsReadDto)[] = ['name', 'location', 'manufacturer', 'model'];
    return assets.filter(asset =>
      searchableFields.some(field => (asset[field] || '').toString().toLowerCase().includes(term))
    );
  }, [sortedAssets, searchText]);

  const handleSearchChange = useCallback((e: { target: { value?: string | number | readonly string[] } }) =>
    setAssetsListSearchText(typeof e.target?.value === 'string' ? e.target.value : ""), [setAssetsListSearchText]);
  const handleClearSearch = useCallback(() => setAssetsListSearchText(""), [setAssetsListSearchText]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh]);

  // Wipe the site's compliance cache, then reload the dashboard so the new values show up.
  const handleClearSiteCache = useCallback(async () => {
    await assetsDashboardApiService.clearSiteComplianceCache();
    await handleRefresh();
  }, [handleRefresh]);


  return (
    <div className='h-[calc(100vh-68px)] bg-gray-100'>
      {!selectedAsset ? (
        // Assets list view
        <div className="flex flex-col h-full min-h-0 overflow-hidden">
          {/* Dashboard tabs — same underline style used on the asset details page. */}
          <div className="pl-3 pr-3 bg-white pt-6 flex flex-wrap items-end justify-between gap-2">
            <ButtonGroup>
              {([
                { idx: 0, label: localizationService.toLanguageString('custom.overview', enMessages.custom.overview) },
                { idx: 1, label: localizationService.toLanguageString('custom.notes', enMessages.custom.notes) },
                { idx: 2, label: localizationService.toLanguageString('custom.siteWorkbook', enMessages.custom.siteWorkbook) },
                ...(isSuperAdmin
                  ? [{ idx: 3, label: localizationService.toLanguageString('custom.insights', enMessages.custom.insights) }]
                  : []),
              ]).map((tab) => (
                <Button
                  key={tab.idx}
                  fillMode="solid"
                  style={{
                    borderRadius: "0px",
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    borderBottom: dashboardTab === tab.idx ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
                    color: dashboardTab === tab.idx ? "var(--kendo-color-primary)" : "inherit",
                  }}
                  onClick={() => setDashboardTab(tab.idx)}
                >
                  {tab.label}
                </Button>
              ))}
            </ButtonGroup>
            {/* Lifted off the tab underline so it doesn't sit flush with the row's bottom edge. */}
            <div className="pb-1 mr-5">
              <ClearCacheButton
                onClear={handleClearSiteCache}
                confirmationMessage={localizationService.toLanguageString('custom.clearSiteCacheConfirmation', enMessages.custom.clearSiteCacheConfirmation)}
              />
            </div>
          </div>
          {dashboardTab === 1 && <DashboardNotesTab />}
          {dashboardTab === 2 && <SiteWorkbookTab />}
          {isSuperAdmin && dashboardTab === 3 && <SiteInsightsTab assets={assetsMetricsData} />}
          {dashboardTab === 0 && (
          <DashboardPanel
            toolbar={
          <div className="flex flex-row gap-2 flex-wrap items-center">
            <div className="flex items-center">
              <ButtonGroup>
                <Tooltip 
                  anchorElement="target" 
                  position="bottom"
                  parentTitle={true}
                >
                  <Button 
                    togglable={true}
                    selected={isTileViewSelected}
                    svgIcon={tableIcon}
                    onClick={() => handleViewChange(true)}
                    title={localizationService.toLanguageString('custom.tileView', enMessages.custom.tileView)}
                  />
                </Tooltip>
                <Tooltip 
                  anchorElement="target" 
                  position="bottom"
                  parentTitle={true}
                >
                  <Button 
                    togglable={true}
                    selected={!isTileViewSelected}
                    svgIcon={alignJustifyIcon}
                    onClick={() => handleViewChange(false)}
                    title={localizationService.toLanguageString('custom.tableView', enMessages.custom.tableView)}
                  />
                </Tooltip>
              </ButtonGroup>
            </div>
            <div className="flex items-center">
              <Tooltip 
                anchorElement="target" 
                position="bottom"
                parentTitle={true}
              >
                <Button 
                  svgIcon={arrowRotateCwIcon}
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  title={localizationService.toLanguageString('custom.refresh', enMessages.custom.refresh)}
                />
              </Tooltip>
            </div>
            <TextBox
              placeholder={localizationService.toLanguageString('custom.search', enMessages.custom.search)}
              onChange={handleSearchChange}
              value={searchText}
              style={{ width: 210, height: 30 }}
              suffix={() => (
                <>
                  {searchText !== "" && searchText !== undefined && (
                    <InputClearValue onClick={handleClearSearch}>
                      <SvgIcon icon={xIcon} />
                    </InputClearValue>
                  )}
                  <InputSeparator />
                </>
              )}
            />
            <SelectAllMultiSelectTree
              groupKey="compliance"
              tagLabel="Compliance"
              selectAllLabel={selectAllL}
              options={complianceOptions}
              value={complianceValues}
              onChange={setAssetsListComplianceValues}
            />
            <SelectAllMultiSelectTree
              groupKey="connectivity"
              tagLabel="Connectivity"
              selectAllLabel={selectAllL}
              options={connectivityOptions}
              value={connectivityValues}
              onChange={setAssetsListConnectivityValues}
            />
            <div className="flex items-center gap-2">
              <Button
                ref={orderByAnchorRef}
                fillMode="outline"
                className="order-by-trigger"
                onClick={() => setOrderByPopupOpen(!orderByPopupOpen)}
              >
                {localizationService.toLanguageString('custom.orderBy', enMessages.custom.orderBy)}
                <SvgIcon icon={chevronDownIcon} size="medium" className="order-by-chevron" style={{ marginLeft: 4 }} />
              </Button>
              <Popup
                anchor={orderByAnchorRef.current?.element ?? undefined}
                show={orderByPopupOpen && !!orderByAnchorRef.current?.element}
                positionMode="fixed"
                popupAlign={{ horizontal: 'left', vertical: 'top' }}
                anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
                margin={{ horizontal: 0, vertical: 4 }}
                popupClass="k-padding-md"
                onClose={() => setOrderByPopupOpen(false)}
                onMouseDownOutside={() => setOrderByPopupOpen(false)}
              >
                <div className="flex flex-col gap-2 py-1 items-center" style={{ minWidth: 140 }}>
                  <div className="flex flex-col gap-1 w-full items-center">
                    {orderByOptions.map((opt) => {
                      const entry = getOrderByEntry(opt.field);
                      return (
                        <Tooltip key={opt.field} anchorElement="target" position="right" parentTitle={true}>
                          <Button
                            togglable={true}
                            selected={!!entry}
                            onClick={() => handleOrderByClick(opt.field)}
                            style={{ minWidth: 130 }}
                            title={entry ? (entry.direction === 'asc' ? sortAscendingL : sortDescendingL) : opt.label}
                          >
                            {opt.label}
                            {entry && (
                              <>
                                <span className="ms-1 mr-0.5 text-xs font-medium" style={{ fontSize: '10px', color: 'white' }}>
                                  {getOrderByIndex(opt.field)}
                                </span>
                                <SvgIcon icon={entry.direction === 'asc' ? arrowUpIcon : arrowDownIcon} size="small" />
                              </>
                            )}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </Popup>
            </div>
          </div>
            }
          >
          <div className="flex-1 min-h-0 overflow-auto relative bg-[#fafafa]">
            {isTileViewSelected && (
              <AssetsDashboardTileView
                assetsMetricsData={searchedAssets}
                onAssetSelect={handleAssetSelect}
                imagesQueries={imagesQueries}
              />
            )}
            {!isTileViewSelected && (
              <AssetsDashboardTableView
                assetsMetricsData={searchedAssets}
                onAssetSelect={handleAssetSelect}
                imagesQueries={imagesQueries}
              />
            )}
            {isFetching && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#fafafa] z-10">
                <LoadingSpinner />
              </div>
            )}
          </div>
          </DashboardPanel>)}
        </div>
      ) : (
        // Asset details view
        <div className="h-full">
          <AssetSelectedPage 
            selectedAsset={selectedAsset}
          />
        </div>
      )}
    </div>
  );
} 